<script setup>
import { h, computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { detectCategoryFromRoute } from 'dashboard/helper/routeCategory';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  ATTENDANCE_PERMISSIONS,
  ASSIGNMENT_POLICY_PERMISSIONS,
  REPORT_AGENT_PERMISSIONS,
  REPORT_CASE_CLOSING_TYPES_PERMISSIONS,
  REPORT_CHAT_HISTORY_EXPORT_PERMISSIONS,
  REPORT_LABEL_PERMISSIONS,
  REPORT_INBOX_PERMISSIONS,
  REPORT_TEAM_PERMISSIONS,
  REPORT_VIP_PERMISSIONS,
  REPORT_VIP_AGENT_PERMISSIONS,
  REPORT_ISSUE_PERMISSIONS,
  REPORT_TOKEN_USAGE_PERMISSIONS,
  LINE_DELIVERY_REPORTS_PERMISSIONS,
  REPORTS_OVERRIDE_PERMISSIONS,
  REPORTS_PERMISSIONS,
  CROSS_BRAND_REPORTS_PERMISSIONS,
  FLEX_TEMPLATE_PERMISSIONS,
} from 'dashboard/constants/permissions';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
  activeCategory: {
    type: String,
    default: '',
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const route = useRoute();
const router = useRouter();
const { accountScopedRoute, currentAccount } = useAccount();

// ── Inbox-settings sidebar ──────────────────────────────────────────────────
const isInboxSettingsRoute = computed(
  () => route.name === 'settings_inbox_show'
);
const currentInboxId = computed(() => route.params?.inboxId);
const currentInboxForSettings = computed(() =>
  inboxes.value.find(i => String(i.id) === String(currentInboxId.value))
);
const isWebWidgetInboxSettings = computed(
  () => currentInboxForSettings.value?.channel_type === 'Channel::WebWidget'
);
const inboxSettingsActiveTab = computed(() => route.query?.tab || '');
const navigateToInboxTab = tabKey => {
  router
    .replace({
      name: route.name,
      params: route.params,
      query: { tab: tabKey },
    })
    .catch(() => {});
};

// ── AiLab assistant settings sidebar (AICloser/AIGamer config) ─────────────
const isAiLabAssistantConfigRoute = computed(() =>
  [
    'ailab_assistant_v2',
    'ailab_ai_closer_assistant_v2',
    'ailab_ai_gamer_assistant_v2',
  ].includes(String(route.name || ''))
);

const normalizeAiLabAssistantTab = raw => {
  const value = (raw || '').toString();
  const map = {
    // Legacy ?tab= values; keys must stay for bookmarks (mirrors assistantV2/Index.vue).
    general: 'get_started',
    advance: 'legacy_advanced',
    // Retired "Policies" tab label; same destination as Train > Guidance.
    policies: 'train_guidance',
    templates: 'train_escalation',
    product: 'train_content',
    preview: 'test_preview',
    simulator: 'test_preview',
    ab: 'test_preview',
    test_simulator: 'test_preview',
    test_ab: 'test_preview',
    inboxes: 'deploy_channels',
    decision_log: 'analyze_decision_log',
  };
  return map[value] || value || 'get_started';
};

const ailabAssistantActiveTab = computed(() =>
  normalizeAiLabAssistantTab(route.query?.tab || 'get_started')
);

const navigateToAiLabAssistantTab = tabKey => {
  router
    .replace({
      name: route.name,
      params: route.params,
      query: {
        ...route.query,
        tab: tabKey,
      },
    })
    .catch(() => {});
};

// AI Closer redesign (P0): collapse the legacy 6-group / 8-tab navigation into
// a 3-section structure (Identity / Knowledge / Deploy) plus an "Advanced"
// fold for power-user tabs (Test, Decision Log, version Settings, etc.).
//
// Scope: AI CLOSER ONLY. AI Gamer keeps the original 6-group layout because the
// team that built AI Gamer has not yet signed off on the redesign — we will
// mirror the new structure into AI Gamer once Closer's UX is validated.
//
// All keys map to the same `?tab=` query values the AssistantV2 shell already
// switches on, so no tab content component / state changes — only the
// presentation order in the sidebar moves around.
const isAiCloserAssistantRoute = computed(
  () => String(route.name || '') === 'ailab_ai_closer_assistant_v2'
);

// Section descriptions live inside each page's content header (which already
// has a title + subtitle pattern) instead of in the sidebar — keeps the
// sidebar scannable as a pure navigation strip.
const aiCloserSidebarGroups = [
  {
    title: 'Identity',
    items: [
      { key: 'get_started', label: 'Personality' },
      { key: 'train_guidance', label: 'Tone & Guidelines' },
    ],
  },
  {
    title: 'Knowledge',
    items: [{ key: 'train_content', label: 'Products, FAQ, Media' }],
  },
  {
    // Renamed from "Deploy" because customer-facing labels should avoid
    // engineering jargon. "Channels" mirrors the channel concept that every
    // social-media-savvy user (LINE / Facebook / WhatsApp) already understands.
    title: 'Channels',
    items: [{ key: 'deploy_channels', label: 'Channels & Inboxes' }],
  },
  {
    title: 'Advanced',
    items: [
      { key: 'test_preview', label: 'Test (Preview)' },
      { key: 'train_escalation', label: 'Escalation rules' },
      { key: 'train_procedures', label: 'Procedures' },
      // Split Analyze (dashboard) from Decision Log (audit log) so each menu
      // label matches its content. Pro SaaS (Linear, Stripe, Intercom) all
      // keep these two concepts on separate menus.
      { key: 'analyze_decision_log', label: 'Analyze' },
      { key: 'legacy_decision_log', label: 'Decision Log' },
      { key: 'settings_general', label: 'Settings' },
    ],
  },
];

const legacyAssistantSidebarGroups = [
  {
    title: 'Get Started',
    items: [{ key: 'get_started', label: 'Get Started' }],
  },
  {
    title: 'Train',
    items: [
      { key: 'train_content', label: 'Content' },
      { key: 'train_guidance', label: 'Guidance' },
      { key: 'train_escalation', label: 'Escalation' },
      { key: 'train_procedures', label: 'Procedures' },
    ],
  },
  {
    title: 'Test',
    items: [{ key: 'test_preview', label: 'Test' }],
  },
  {
    title: 'Deploy',
    items: [{ key: 'deploy_channels', label: 'Channels & Inboxes' }],
  },
  {
    title: 'Analyze',
    items: [{ key: 'analyze_decision_log', label: 'Analyze' }],
  },
  {
    title: 'Settings',
    items: [{ key: 'settings_general', label: 'Basics' }],
  },
];

const ailabAssistantSidebarGroups = computed(() =>
  isAiCloserAssistantRoute.value
    ? aiCloserSidebarGroups
    : legacyAssistantSidebarGroups
);
// ───────────────────────────────────────────────────────────────────────────
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t, te } = useI18n();
const inboxCollaboratorsLabel = computed(() => {
  if (te('INBOX_MGMT.INBOX_SETTINGS_NAV.COLLABORATORS')) {
    return t('INBOX_MGMT.INBOX_SETTINGS_NAV.COLLABORATORS');
  }
  if (te('INBOX_MGMT.TABS.COLLABORATORS')) {
    return t('INBOX_MGMT.TABS.COLLABORATORS');
  }
  return 'Collaborators';
});
const effectiveActiveCategory = computed(() =>
  detectCategoryFromRoute(route, props.activeCategory || '')
);
const normalizedActiveCategory = computed(() =>
  String(effectiveActiveCategory.value || '').toLowerCase()
);
const isReportsCategory = computed(
  () => normalizedActiveCategory.value === 'reports'
);
const isPortalsCategory = computed(
  () => normalizedActiveCategory.value === 'portals'
);
const isReportsLikeCategory = computed(
  () => isReportsCategory.value || isPortalsCategory.value
);
const isCampaignsCategory = computed(
  () => normalizedActiveCategory.value === 'campaigns'
);
const isQaDashboardCategory = computed(
  () => normalizedActiveCategory.value === 'qadashboard'
);
const isEngageCategory = computed(
  () => normalizedActiveCategory.value === 'engage'
);
const isTeamManagementCategory = computed(
  () => normalizedActiveCategory.value === 'teammanagement'
);
const isConversationCategory = computed(
  () => normalizedActiveCategory.value === 'conversation'
);
const isWorkforceCategory = computed(
  () => normalizedActiveCategory.value === 'workforce'
);
const isSettingsCategory = computed(
  () => normalizedActiveCategory.value === 'settings'
);
const isContactsCategory = computed(
  () => normalizedActiveCategory.value === 'contacts'
);
const isAiLabCategory = computed(
  () => normalizedActiveCategory.value === 'ailab'
);
const isVoiceCategory = computed(
  () => normalizedActiveCategory.value === 'voice'
);

const isWideSidebarCategory = computed(
  () =>
    isReportsCategory.value ||
    isPortalsCategory.value ||
    isCampaignsCategory.value ||
    isQaDashboardCategory.value ||
    isSettingsCategory.value ||
    isContactsCategory.value ||
    isAiLabCategory.value ||
    isWorkforceCategory.value ||
    isVoiceCategory.value
);

const routeName = computed(() => String(route?.name || ''));
const routePath = computed(() => String(route?.path || ''));
const shouldHideSidebar = computed(() => {
  // Prefer route meta when available; Engage/TeamManagement already set this.
  if (route?.meta?.hideSecondarySidebar) return true;

  // Hard-hide for specific sections, even if activeCategory is wrong on refresh.
  const isEngageRoute =
    routeName.value.startsWith('engage_') ||
    routePath.value.includes('/engage');
  const isTeamManagementRoute =
    routeName.value.startsWith('team_management_') ||
    routePath.value.includes('/team-management');

  return (
    isEngageRoute ||
    isTeamManagementRoute ||
    isEngageCategory.value ||
    isTeamManagementCategory.value
  );
});

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// We're using localStorage to store the expanded items in the sidebar
// Changed to array to allow multiple items to be expanded at once
// Default: only 'All Conversations' is expanded
const expandedItems = useStorage(
  'next-sidebar-expanded-items',
  ['All Conversations'],
  sessionStorage
);

// Toggle function for multi-expand support
const setExpandedItem = name => {
  if (Array.isArray(expandedItems.value)) {
    if (expandedItems.value.includes(name)) {
      expandedItems.value = expandedItems.value.filter(n => n !== name);
    } else {
      expandedItems.value = [...expandedItems.value, name];
    }
  } else {
    // Migration from old single-value format
    expandedItems.value = [name];
  }
};

// Helper to check if an item is expanded
const isItemExpanded = name => {
  if (Array.isArray(expandedItems.value)) {
    return expandedItems.value.includes(name);
  }
  return expandedItems.value === name;
};

provideSidebarContext({
  expandedItems,
  setExpandedItem,
  isItemExpanded,
  isReportsSidebar: isReportsLikeCategory,
  isConversationSidebar: isConversationCategory,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getTeams');
const issues = useMapGetter('issues/getIssues');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('issues/get');
  store.dispatch('vipLevels/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const currentUser = useMapGetter('getCurrentUser');

// Inbox Archive (Phase 1C) — drop archived inboxes from the Channels list and
// the QA Live Monitor list. The filter is a no-op for inboxes that aren't
// archived (archived_at IS NULL) so accounts that haven't opted into
// inbox_archive see no behavior change. Archived inboxes remain reachable from
// Settings → Archived Inboxes.
const sortedInboxes = computed(() =>
  inboxes.value
    .slice()
    .filter(i => !i.archived_at)
    .sort((a, b) => a.name.localeCompare(b.name))
);
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_AGENT_PERMISSIONS,
    ],
  },
  {
    name: 'Reports Case Closing Types',
    label: t('SIDEBAR.REPORTS_CASE_CLOSING_TYPES'),
    to: accountScopedRoute('case_closing_types_reports'),
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_CASE_CLOSING_TYPES_PERMISSIONS,
    ],
  },
  {
    name: 'Reports Chat History Export',
    label: t('SIDEBAR.REPORTS_CHAT_HISTORY_EXPORT'),
    to: accountScopedRoute('chat_history_export_reports'),
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_CHAT_HISTORY_EXPORT_PERMISSIONS,
    ],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_LABEL_PERMISSIONS,
    ],
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_INBOX_PERMISSIONS,
    ],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_TEAM_PERMISSIONS,
    ],
  },
  {
    name: 'Reports VIP',
    label: t('SIDEBAR.REPORTS_VIP'),
    to: accountScopedRoute('vip_reports_index'),
    activeOn: ['vip_reports_show'],
    permissions: ['administrator', REPORTS_PERMISSIONS, REPORT_VIP_PERMISSIONS],
  },
  {
    name: 'Reports VIP Agents',
    label: t('SIDEBAR.REPORTS_VIP_AGENT'),
    to: accountScopedRoute('vip_agent_reports_index'),
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_VIP_AGENT_PERMISSIONS,
    ],
  },
  {
    name: 'Reports Issues',
    label: t('SIDEBAR.REPORTS_ISSUE'),
    to: accountScopedRoute('issue_reports_index'),
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_ISSUE_PERMISSIONS,
    ],
  },
  {
    name: 'Reports Line Delivery',
    label: t('SIDEBAR.REPORTS_LINE_DELIVERY'),
    to: accountScopedRoute('line_delivery_reports'),
    permissions: ['administrator', LINE_DELIVERY_REPORTS_PERMISSIONS],
  },
  {
    name: 'Reports QA Overrides',
    label: t('SIDEBAR.REPORTS_QA_OVERRIDES'),
    to: accountScopedRoute('qa_overrides_reports_index'),
    permissions: ['administrator', REPORTS_OVERRIDE_PERMISSIONS],
  },
  {
    name: 'Reports All Brands',
    label: t('SIDEBAR.REPORTS_ALL_BRANDS'),
    to: accountScopedRoute('cross_brand_reports_index'),
    permissions: ['administrator', CROSS_BRAND_REPORTS_PERMISSIONS],
  },
  {
    name: 'Reports Token Usage',
    label: t('SIDEBAR.REPORTS_TOKEN_USAGE'),
    to: accountScopedRoute('token_usage_reports'),
    permissions: [
      'administrator',
      REPORTS_PERMISSIONS,
      REPORT_TOKEN_USAGE_PERMISSIONS,
    ],
  },
];

const userPermissions = computed(() =>
  getUserPermissions(currentUser.value, currentAccount.value?.id)
);

const userRole = computed(() => currentAccount.value?.role);

const hasRoutePermission = route => {
  if (!route.permissions) {
    return true;
  }
  return hasPermissions(route.permissions, userPermissions.value);
};

const canAccessSearch = computed(() => {
  const resolvedSearchRoute = router.resolve(accountScopedRoute('search'));
  return hasRoutePermission({
    permissions: resolvedSearchRoute?.meta?.permissions || [],
  });
});

const reportRoutes = computed(() =>
  newReportRoutes().filter(route => hasRoutePermission(route))
);

const isWorkforceEnabled = computed(() => {
  if (!currentAccount.value?.id) return false;
  return isFeatureEnabledonAccount.value(
    currentAccount.value.id,
    FEATURE_FLAGS.WORKFORCE_MANAGEMENT
  );
});

const isAiLabEnabled = computed(() => {
  if (!currentAccount.value?.id) return false;
  return isFeatureEnabledonAccount.value(
    currentAccount.value.id,
    FEATURE_FLAGS.CAPTAIN
  );
});

const isHelpCenterEnabled = computed(() => {
  if (!currentAccount.value?.id) return false;
  return isFeatureEnabledonAccount.value(
    currentAccount.value.id,
    FEATURE_FLAGS.HELP_CENTER
  );
});

const isInboxArchiveEnabled = computed(() => {
  if (!currentAccount.value?.id) return false;
  return isFeatureEnabledonAccount.value(
    currentAccount.value.id,
    FEATURE_FLAGS.INBOX_ARCHIVE
  );
});

const workforceMenu = computed(() => {
  if (!isWorkforceEnabled.value) return null;
  const children = [
    {
      name: 'WorkforceMyShift',
      label: t('WORKFORCE.SIDEBAR.MY_SHIFT'),
      to: accountScopedRoute('workforce_my_shift'),
      permissions: ['administrator', 'agent', ATTENDANCE_PERMISSIONS],
    },
    {
      name: 'WorkforceSchedules',
      label: t('WORKFORCE.SIDEBAR.SCHEDULES'),
      to: accountScopedRoute('workforce_schedules'),
      permissions: ['administrator', ASSIGNMENT_POLICY_PERMISSIONS],
    },
    {
      name: 'WorkforceAssignment',
      label: t('WORKFORCE.SIDEBAR.ASSIGNMENT'),
      to: accountScopedRoute('workforce_assignment'),
      permissions: ['administrator', ASSIGNMENT_POLICY_PERMISSIONS],
    },
    {
      name: 'WorkforceAttendance',
      label: t('WORKFORCE.SIDEBAR.ATTENDANCE'),
      to: accountScopedRoute('workforce_attendance'),
      permissions: ['administrator', ATTENDANCE_PERMISSIONS],
    },
    {
      name: 'WorkforceLive',
      label: t('WORKFORCE.SIDEBAR.LIVE'),
      to: accountScopedRoute('workforce_live'),
      permissions: ['administrator', ATTENDANCE_PERMISSIONS],
    },
  ].filter(route => hasRoutePermission(route));

  if (!children.length) return null;

  return {
    name: 'Workforce',
    label: t('WORKFORCE.SIDEBAR.LABEL'),
    icon: 'i-lucide-briefcase-business',
    children,
  };
});

const META_CACHE_TTL_MS = 5 * 1000;
const UNREAD_REFRESH_THROTTLE_MS = 250;
const unreadMetaCache = new Map();
const unreadMetaInFlight = new Map();
let unreadRefreshTimer = null;
let unreadRefreshInProgress = false;
let unreadRefreshQueued = false;

const buildMetaCacheKey = params =>
  Object.entries(params || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}:${[...value].sort().join(',')}`;
      }
      return `${key}:${value ?? ''}`;
    })
    .join('|');

const pruneUnreadMetaCache = () => {
  const expiryTime = Date.now() - META_CACHE_TTL_MS;
  unreadMetaCache.forEach((entry, key) => {
    if (!entry || entry.ts < expiryTime) {
      unreadMetaCache.delete(key);
    }
  });
};

const fetchUnreadCountByMeta = async params => {
  const key = buildMetaCacheKey(params);
  const now = Date.now();
  const cached = unreadMetaCache.get(key);

  if (cached && now - cached.ts < META_CACHE_TTL_MS) {
    return cached.value;
  }

  if (unreadMetaInFlight.has(key)) {
    return unreadMetaInFlight.get(key);
  }

  const request = ConversationApi.meta(params)
    .then(({ data }) => {
      const unread =
        Number(data?.meta?.unread_count ?? data?.unread_count ?? 0) || 0;
      unreadMetaCache.set(key, { value: unread, ts: Date.now() });
      return unread;
    })
    .catch(() => 0)
    .finally(() => {
      unreadMetaInFlight.delete(key);
    });

  unreadMetaInFlight.set(key, request);
  return request;
};

// -------- Unread counts (Issues / Teams / Inboxes / Labels) — batch fetch ----------
const issueUnreadCounts = ref({});
const issueGroupUnreadTotal = computed(() =>
  Object.values(issueUnreadCounts.value).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  )
);

const teamUnreadCounts = ref({});
const teamGroupUnreadTotal = computed(() =>
  Object.values(teamUnreadCounts.value).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  )
);

const inboxUnreadCounts = ref({});
const inboxGroupUnreadTotal = computed(() =>
  Object.values(inboxUnreadCounts.value).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  )
);

const labelUnreadCounts = ref({});
const labelGroupUnreadTotal = computed(() =>
  Object.values(labelUnreadCounts.value).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  )
);

// Single batch call replaces N individual /meta calls
async function fetchBatchUnreadCounts() {
  const inboxList = inboxes.value || [];
  const teamList = teams.value || [];
  const labelList = labels.value || [];
  const issueList = issues.value || [];

  const hasAny =
    inboxList.length || teamList.length || labelList.length || issueList.length;
  if (!hasAny) return;

  try {
    const { data } = await ConversationApi.batchMeta({
      inboxIds: inboxList.map(i => i.id),
      teamIds: teamList.map(t => t.id),
      labelTitles: labelList.map(l => l.title),
      issueIds: issueList.map(i => i.id),
    });

    // inboxes  → keyed by numeric id
    const nextInboxes = {};
    inboxList.forEach(i => {
      nextInboxes[i.id] = Number(data.inboxes?.[i.id]) || 0;
    });
    inboxUnreadCounts.value = nextInboxes;

    // teams → keyed by numeric id
    const nextTeams = {};
    teamList.forEach(t => {
      nextTeams[t.id] = Number(data.teams?.[t.id]) || 0;
    });
    teamUnreadCounts.value = nextTeams;

    // labels → keyed by label id, looked up by title
    const nextLabels = {};
    labelList.forEach(l => {
      nextLabels[l.id] = Number(data.labels?.[l.title]) || 0;
    });
    labelUnreadCounts.value = nextLabels;

    // issues → keyed by numeric id
    const nextIssues = {};
    issueList.forEach(i => {
      nextIssues[i.id] = Number(data.issues?.[i.id]) || 0;
    });
    issueUnreadCounts.value = nextIssues;
  } catch (e) {
    // fall back silently — counts stay at last known values
  }
}

// Keep individual watchers so counts refresh when lists change
function fetchIssueUnreadCounts() {
  fetchBatchUnreadCounts();
}
function fetchTeamUnreadCounts() {
  fetchBatchUnreadCounts();
}
function fetchInboxUnreadCounts() {
  fetchBatchUnreadCounts();
}
function fetchLabelUnreadCounts() {
  fetchBatchUnreadCounts();
}

// -------- Unread counts (All Conversations tabs) ----------
const allUnreadCount = ref(0);
const unassignedUnreadCount = ref(0);
const mineUnreadCount = ref(0);
const unreadTabCount = ref(0);
const allConversationsUnreadCount = computed(
  () =>
    allUnreadCount.value +
    unassignedUnreadCount.value +
    mineUnreadCount.value +
    unreadTabCount.value
);

// -------- Unread counts (VIP, Unattended) ----------
const vipUnreadCount = ref(0);
const unattendedUnreadCount = ref(0);

// -------- Unread counts (Wlab AI Agent) ----------
const wlabAiAllUnreadCount = ref(0);
const wlabAiResolvedUnreadCount = ref(0);
const wlabAiHandoffUnreadCount = ref(0);
const wlabAiPendingUnreadCount = ref(0);

async function fetchConversationTabUnreadCounts() {
  try {
    const [
      allUnread,
      unassignedUnread,
      mineUnread,
      vipUnread,
      unattendedUnread,
    ] = await Promise.all([
      fetchUnreadCountByMeta({ status: 'open', assigneeType: 'all' }),
      fetchUnreadCountByMeta({ status: 'open', assigneeType: 'unassigned' }),
      fetchUnreadCountByMeta({ status: 'open', assigneeType: 'me' }),
      fetchUnreadCountByMeta({
        status: 'open',
        assigneeType: 'all',
        conversationType: 'vip',
      }),
      fetchUnreadCountByMeta({
        status: 'open',
        assigneeType: 'all',
        conversationType: 'unattended',
      }),
    ]);

    allUnreadCount.value = allUnread;
    unassignedUnreadCount.value = unassignedUnread;
    mineUnreadCount.value = mineUnread;
    unreadTabCount.value = allUnread;
    vipUnreadCount.value = vipUnread;
    unattendedUnreadCount.value = unattendedUnread;
  } catch (e) {
    // ignore
  }
}

async function fetchWlabAiAgentUnreadCounts() {
  try {
    const [allRes, resolvedRes, handoffRes, pendingRes] = await Promise.all([
      ConversationApi.meta({
        status: 'open',
        assigneeType: 'all',
        conversationType: 'wlab_ai_agent_all',
      }),
      ConversationApi.meta({
        status: 'open',
        assigneeType: 'all',
        conversationType: 'wlab_ai_agent_resolved',
      }),
      ConversationApi.meta({
        status: 'open',
        assigneeType: 'all',
        conversationType: 'wlab_ai_agent_handoff',
      }),
      ConversationApi.meta({
        status: 'open',
        assigneeType: 'all',
        conversationType: 'wlab_ai_agent_pending',
      }),
    ]);

    wlabAiAllUnreadCount.value =
      allRes.data?.meta?.unread_count ?? allRes.data?.unread_count ?? 0;
    wlabAiResolvedUnreadCount.value =
      resolvedRes.data?.meta?.unread_count ??
      resolvedRes.data?.unread_count ??
      0;
    wlabAiHandoffUnreadCount.value =
      handoffRes.data?.meta?.unread_count ?? handoffRes.data?.unread_count ?? 0;
    wlabAiPendingUnreadCount.value =
      pendingRes.data?.meta?.unread_count ?? pendingRes.data?.unread_count ?? 0;
  } catch (e) {
    // ignore
  }
}

// Watchers for unread counts
watch(issues, () => {
  fetchIssueUnreadCounts();
});

watch(teams, () => {
  fetchTeamUnreadCounts();
});

watch(inboxes, () => {
  fetchInboxUnreadCounts();
});

watch(labels, () => {
  fetchLabelUnreadCounts();
});

// Refresh all sidebar unread counts (batch API for inbox/team/label/issue)
const refreshAllUnreadCounts = () => {
  Promise.all([
    fetchConversationTabUnreadCounts(),
    fetchWlabAiAgentUnreadCounts(),
    fetchBatchUnreadCounts(),
  ]).catch(() => {});
};

const reportsSidebarGroups = computed(() => [
  {
    name: 'Reports Group Overview',
    label: 'Overview',
    children: [
      {
        name: 'Report Last 7 Days',
        label: t('SIDEBAR.REPORTS_LAST_7_DAYS'),
        to: accountScopedRoute('account_last_7_days_reports'),
      },
      {
        name: 'Report Dashboard',
        label: t('SIDEBAR.REPORTS_DASHBOARD'),
        to: accountScopedRoute('account_overview_reports'),
      },
    ],
  },
  {
    name: 'Reports Group Chats',
    label: 'Chats',
    children: [
      {
        name: 'Report Total Chats',
        label: t('SIDEBAR.REPORTS_TOTAL_CHATS'),
        to: accountScopedRoute('total_chats_reports'),
      },
      {
        name: 'Report Conversation',
        label: t('SIDEBAR.REPORTS_CHAT_ENGAGEMENT'),
        to: accountScopedRoute('chat_engagement_reports'),
      },
      {
        name: 'Report Missed Chats',
        label: t('SIDEBAR.REPORTS_MISSED_CHATS'),
        to: accountScopedRoute('missed_chats_reports'),
      },
      {
        name: 'Reports CSAT',
        label: t('CSAT_REPORTS.HEADER'),
        to: accountScopedRoute('csat_reports'),
      },
      {
        name: 'Reports SLA',
        label: t('SIDEBAR.REPORTS_SLA'),
        to: accountScopedRoute('sla_reports'),
      },
      {
        name: 'Report Tags Usage',
        label: t('SIDEBAR.REPORTS_TAGS_USAGE'),
        to: accountScopedRoute('tags_usage_reports'),
      },
      {
        name: 'Report Campaigns Conversion',
        label: t('SIDEBAR.REPORTS_CAMPAIGNS_CONVERSION'),
        to: accountScopedRoute('campaigns_conversion_reports'),
      },
      {
        name: 'Reports Top Customer Questions',
        label: t('SIDEBAR.REPORTS_TOP_CUSTOMER_QUESTIONS'),
        to: accountScopedRoute('top_customer_questions_reports'),
      },
    ],
  },
  {
    name: 'Reports Group Service',
    label: 'Service levels',
    children: [
      {
        name: 'Reports Chat Response Times',
        label: t('SIDEBAR.REPORTS_CHAT_RESPONSE_TIMES'),
        to: accountScopedRoute('chat_response_times_reports'),
      },
      {
        name: 'Reports Chat Availability',
        label: t('SIDEBAR.REPORTS_CHAT_AVAILABILITY'),
        to: accountScopedRoute('chat_availability_reports'),
      },
      {
        name: 'Reports Chat Duration',
        label: t('SIDEBAR.REPORTS_CHAT_DURATION'),
        to: accountScopedRoute('chat_duration_reports'),
      },
      {
        name: 'Reports Queued Customers',
        label: t('SIDEBAR.REPORTS_QUEUED_CUSTOMERS'),
        to: accountScopedRoute('queued_customers_reports'),
      },
    ],
  },
  {
    name: 'Reports Group Operations',
    label: 'Operations',
    children: [
      {
        name: 'Reports Agent',
        label: t('SIDEBAR.REPORTS_AGENT'),
        to: accountScopedRoute('agent_reports_index'),
        activeOn: ['agent_reports_show'],
      },
      {
        name: 'Reports Agent Activity',
        label: t('SIDEBAR.REPORTS_AGENT_ACTIVITY'),
        to: accountScopedRoute('agent_activity_reports'),
      },
      {
        name: 'Reports Agent Chat Status',
        label: t('SIDEBAR.REPORTS_AGENT_CHAT_STATUS'),
        to: accountScopedRoute('agent_chat_status_reports'),
      },
      {
        name: 'Reports Staffing Prediction',
        label: t('SIDEBAR.REPORTS_STAFFING_PREDICTION'),
        to: accountScopedRoute('staffing_prediction_reports'),
      },
      {
        name: 'Reports Team',
        label: t('SIDEBAR.REPORTS_TEAM'),
        to: accountScopedRoute('team_reports_index'),
        activeOn: ['team_reports_show'],
      },
      {
        name: 'Reports Inbox',
        label: t('SIDEBAR.REPORTS_INBOX'),
        to: accountScopedRoute('inbox_reports_index'),
        activeOn: ['inbox_reports_show'],
      },
      {
        name: 'Reports Label',
        label: t('SIDEBAR.REPORTS_LABEL'),
        to: accountScopedRoute('label_reports_index'),
        activeOn: ['label_reports_show'],
      },
      {
        name: 'Reports Issues',
        label: t('SIDEBAR.REPORTS_ISSUE'),
        to: accountScopedRoute('issue_reports_index'),
      },
      {
        name: 'Reports VIP',
        label: t('SIDEBAR.REPORTS_VIP'),
        to: accountScopedRoute('vip_reports_index'),
        activeOn: ['vip_reports_show'],
      },
      {
        name: 'Reports VIP Agents',
        label: t('SIDEBAR.REPORTS_VIP_AGENT'),
        to: accountScopedRoute('vip_agent_reports_index'),
      },
    ],
  },
  {
    name: 'Reports Group Automation',
    label: 'Automation',
    children: [
      {
        name: 'Reports Bot',
        label: t('SIDEBAR.REPORTS_BOT'),
        to: accountScopedRoute('bot_reports'),
      },
      {
        name: 'Reports Telegram Bot',
        label: t('SIDEBAR.REPORTS_TELEGRAM_BOT'),
        to: accountScopedRoute('telegram_bot_reports'),
      },
      {
        name: 'Reports Token Usage',
        label: t('SIDEBAR.REPORTS_TOKEN_USAGE'),
        to: accountScopedRoute('token_usage_reports'),
      },
    ],
  },
  {
    name: 'Reports Group Data',
    label: 'Data & admin',
    children: [
      {
        name: 'Reports Live Chat Forms Export',
        label: t('SIDEBAR.REPORTS_LIVE_CHAT_FORMS_EXPORT'),
        to: accountScopedRoute('live_chat_forms_export_reports'),
      },
      {
        name: 'Reports Chat History Export',
        label: t('SIDEBAR.REPORTS_CHAT_HISTORY_EXPORT'),
        to: accountScopedRoute('chat_history_export_reports'),
      },
      {
        name: 'Reports Case Closing Types',
        label: t('SIDEBAR.REPORTS_CASE_CLOSING_TYPES'),
        to: accountScopedRoute('case_closing_types_reports'),
      },
      {
        name: 'Reports Line Delivery',
        label: t('SIDEBAR.REPORTS_LINE_DELIVERY'),
        to: accountScopedRoute('line_delivery_reports'),
      },
      {
        name: 'Reports QA Overrides',
        label: t('SIDEBAR.REPORTS_QA_OVERRIDES'),
        to: accountScopedRoute('qa_overrides_reports_index'),
      },
      {
        name: 'Reports All Brands',
        label: t('SIDEBAR.REPORTS_ALL_BRANDS'),
        to: accountScopedRoute('cross_brand_reports_index'),
      },
    ],
  },
]);

onMounted(() => {
  refreshAllUnreadCounts();
  // Listen for unread count refresh events (triggered when marking messages read/unread)
  emitter.on(BUS_EVENTS.REFRESH_SIDEBAR_UNREAD_COUNTS, refreshAllUnreadCounts);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.REFRESH_SIDEBAR_UNREAD_COUNTS, refreshAllUnreadCounts);
  if (unreadRefreshTimer) {
    clearTimeout(unreadRefreshTimer);
    unreadRefreshTimer = null;
  }
});

const menuItems = computed(() => {
  return [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All Conversations',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-lucide-messages-square',
          activeOn: ['inbox_conversation'],
          headerCount: allConversationsUnreadCount.value,
          children: [
            {
              name: 'Conversations All',
              label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.all'),
              to: accountScopedRoute('home', {}, { assignee: 'all' }),
              count: allUnreadCount.value,
              activeOn: ['home', 'inbox_conversation'],
            },
            {
              name: 'Conversations Unassigned',
              label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.unassigned'),
              to: accountScopedRoute('home', {}, { assignee: 'unassigned' }),
              count: unassignedUnreadCount.value,
              activeOn: ['home', 'inbox_conversation'],
            },
            {
              name: 'Conversations Mine',
              label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.me'),
              to: accountScopedRoute('home', {}, { assignee: 'me' }),
              count: mineUnreadCount.value,
              activeOn: ['home', 'inbox_conversation'],
            },
            {
              name: 'Conversations Unread',
              label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.unread'),
              to: accountScopedRoute('home', {}, { assignee: 'unread' }),
              count: unreadTabCount.value,
              activeOn: ['home', 'inbox_conversation'],
            },
          ],
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          icon: 'i-lucide-at-sign',
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          icon: 'i-lucide-clock',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
          count: unattendedUnreadCount.value,
        },
        {
          name: 'VIP',
          label: t('SIDEBAR.VIP'),
          icon: 'i-lucide-crown',
          activeOn: ['conversation_through_vip', 'conversation_vip'],
          to: accountScopedRoute('conversation_vip'),
          count: vipUnreadCount.value,
        },
        {
          name: 'Wlab AI Agent',
          label: 'Wlab AI Agent',
          icon: 'i-lucide-bot',
          headerCount: wlabAiAllUnreadCount.value,
          activeOn: [
            'conversation_wlab_ai_agent_all',
            'conversation_through_wlab_ai_agent_all',
            'conversation_wlab_ai_agent_resolved',
            'conversation_through_wlab_ai_agent_resolved',
            'conversation_wlab_ai_agent_handoff',
            'conversation_through_wlab_ai_agent_handoff',
            'conversation_wlab_ai_agent_pending',
            'conversation_through_wlab_ai_agent_pending',
          ],
          children: [
            {
              name: 'Wlab AI Agent - All',
              label: 'All Conversations',
              to: accountScopedRoute('conversation_wlab_ai_agent_all'),
              count: wlabAiAllUnreadCount.value,
              activeOn: [
                'conversation_wlab_ai_agent_all',
                'conversation_through_wlab_ai_agent_all',
              ],
            },
            {
              name: 'Wlab AI Agent - Resolved',
              label: 'Resolved',
              to: accountScopedRoute('conversation_wlab_ai_agent_resolved'),
              count: wlabAiResolvedUnreadCount.value,
              activeOn: [
                'conversation_wlab_ai_agent_resolved',
                'conversation_through_wlab_ai_agent_resolved',
              ],
            },
            {
              name: 'Wlab AI Agent - Escalated',
              label: 'Escalated & Handoff',
              to: accountScopedRoute('conversation_wlab_ai_agent_handoff'),
              count: wlabAiHandoffUnreadCount.value,
              activeOn: [
                'conversation_wlab_ai_agent_handoff',
                'conversation_through_wlab_ai_agent_handoff',
              ],
            },
            {
              name: 'Wlab AI Agent - Pending',
              label: 'Pending',
              to: accountScopedRoute('conversation_wlab_ai_agent_pending'),
              count: wlabAiPendingUnreadCount.value,
              activeOn: [
                'conversation_wlab_ai_agent_pending',
                'conversation_through_wlab_ai_agent_pending',
              ],
            },
          ],
        },
        {
          name: 'Issue',
          label: t('SIDEBAR.ISSUE'),
          icon: 'i-lucide-flag',
          activeOn: ['conversations_through_issue'],
          headerCount: issueGroupUnreadTotal.value,
          children: issues.value.map(issue => ({
            name: `issue-${issue.name}-${issue.id}`,
            label: formatName(issue.name),
            to: {
              ...accountScopedRoute('issue_conversations', {
                issueId: issue.id,
              }),
              query: { source: 'issue' },
            },
            count: issueUnreadCounts.value[issue.id] || 0,
            activeOn: [], // 防止被路径匹配激活
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          headerCount: teamGroupUnreadTotal.value,
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: formatName(team.name),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
            count: teamUnreadCounts.value[team.id] || 0,
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          headerCount: inboxGroupUnreadTotal.value,
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            count: inboxUnreadCounts.value[inbox.id] || 0,
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
                count: inboxUnreadCounts.value[inbox.id] || 0,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          headerCount: labelGroupUnreadTotal.value,
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
            count: labelUnreadCounts.value[label.id] || 0,
          })),
        },
      ],
    },
    {
      name: 'AiLab',
      icon: 'i-ph-robot-bold',
      label: t('SIDEBAR.AILAB'),
      children: [
        // AI Studio = new SMB-friendly entry point. Listed first so new
        // customers land on the simpler workflow by default. AI Closer /
        // AI Gamer remain available for power users and existing assistants.
        {
          name: 'AI Studio',
          label: 'AI Studio',
          icon: 'i-lucide-wand-2',
          to: accountScopedRoute('ailab_ai_studio_index'),
          activeOn: ['ailab_ai_studio_manage'],
        },
        {
          name: 'AI Closer',
          label: 'AI Closer',
          icon: 'i-lucide-sparkles',
          to: accountScopedRoute('ailab_ai_closer_index'),
          activeOn: ['ailab_ai_closer_assistant_v2'],
        },
        {
          name: 'AI gamer',
          label: 'AI gamer',
          icon: 'i-lucide-bot',
          to: accountScopedRoute('ailab_ai_gamer_index'),
          activeOn: ['ailab_ai_gamer_assistant_v2'],
        },
        {
          name: 'Brand Training',
          label: 'Brand Training',
          icon: 'i-lucide-brain',
          to: accountScopedRoute('ailab_brand_training_dashboard'),
          activeOn: [
            'ailab_brand_training_assistant',
            'ailab_brand_training_knowledge',
          ],
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Engage',
      label: t('SIDEBAR.ENGAGE'),
      icon: 'i-lucide-activity',
      children: [
        {
          name: 'Engage Traffic',
          label: t('SIDEBAR.ENGAGE_TRAFFIC'),
          to: accountScopedRoute('engage_traffic_index'),
          activeOn: ['engage_traffic_index'],
        },
        {
          name: 'Engage Campaigns',
          label: t('SIDEBAR.ENGAGE_CAMPAIGNS'),
          to: accountScopedRoute('engage_campaigns_index'),
          activeOn: ['engage_campaigns_index'],
        },
        {
          name: 'Engage Goals',
          label: t('SIDEBAR.ENGAGE_GOALS'),
          to: accountScopedRoute('engage_goals_index'),
          activeOn: ['engage_goals_index'],
        },
      ],
    },
    {
      name: 'TeamManagement',
      label: t('SIDEBAR.TEAM_MANAGEMENT'),
      icon: 'i-lucide-users-round',
      to: accountScopedRoute('team_management_agents'),
      activeOn: ['team_management_agents'],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: reportsSidebarGroups.value,
    },
    {
      name: 'QADashboard',
      label: t('SIDEBAR.QA_DASHBOARD'),
      icon: 'i-lucide-shield-check',
      children: [
        {
          name: 'QALiveMonitor',
          label: 'QA Live Monitor',
          icon: 'i-lucide-monitor',
          to: accountScopedRoute('qa_monitor_home'),
          activeOn: [
            'qa_monitor_home',
            'qa_monitor_conversation',
            'qa_monitor_inbox',
            'qa_monitor_conversation_through_inbox',
          ],
          children: [
            {
              name: 'QALiveMonitorAll',
              label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.all'),
              to: accountScopedRoute('qa_monitor_home'),
              activeOn: ['qa_monitor_home', 'qa_monitor_conversation'],
            },
            {
              name: 'QALiveMonitorChannels',
              label: t('SIDEBAR.CHANNELS'),
              icon: 'i-lucide-mailbox',
              activeOn: [
                'qa_monitor_inbox',
                'qa_monitor_conversation_through_inbox',
              ],
              children: sortedInboxes.value.map(inbox => ({
                name: `qa-monitor-${inbox.name}-${inbox.id}`,
                label: inbox.name,
                to: accountScopedRoute('qa_monitor_inbox', {
                  inbox_id: inbox.id,
                }),
                component: leafProps =>
                  h(ChannelLeaf, {
                    label: leafProps.label,
                    active: leafProps.active,
                    inbox,
                    count: null,
                  }),
              })),
            },
          ],
        },
        {
          name: 'QA Template Audit',
          label: t('SIDEBAR.QA_TEMPLATE_AUDIT'),
          to: accountScopedRoute('qa_dashboard_template_audit'),
        },
        {
          name: 'QA Close-Type Audit',
          label: t('SIDEBAR.QA_CLOSE_TYPE_AUDIT'),
          to: accountScopedRoute('qa_dashboard_close_type_audit'),
        },
        {
          name: 'QA Keyword Search',
          label: t('SIDEBAR.QA_KEYWORD_SEARCH'),
          to: accountScopedRoute('qa_dashboard_keyword_search'),
        },
      ],
    },
    ...(workforceMenu.value ? [workforceMenu.value] : []),
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Telegram',
          label: t('SIDEBAR.TELEGRAM'),
          to: accountScopedRoute('campaigns_telegram_index'),
        },
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
        {
          name: 'LINE',
          label: t('SIDEBAR.LINE'),
          to: accountScopedRoute('campaigns_line_index'),
        },
      ],
    },
    {
      // AI Voice — Sprint 1+ manager surface (§10.4). Children grow as
      // outbound campaigns + compliance dashboard ship in later sprints.
      name: 'Voice',
      label: t('SIDEBAR.VOICE.TITLE'),
      icon: 'i-lucide-phone',
      children: [
        {
          name: 'Voice Overview',
          label: t('SIDEBAR.VOICE.OVERVIEW'),
          activeOn: ['voice_overview'],
          to: accountScopedRoute('voice_overview'),
        },
        {
          name: 'Voice Calls',
          label: t('SIDEBAR.VOICE.CALLS'),
          activeOn: ['voice_calls_list', 'voice_call_detail'],
          to: accountScopedRoute('voice_calls_list'),
        },
        {
          name: 'Voice Campaigns',
          label: t('SIDEBAR.VOICE.CAMPAIGNS'),
          activeOn: [
            'voice_campaigns_list',
            'voice_campaign_detail',
            'voice_campaign_new',
          ],
          to: accountScopedRoute('voice_campaigns_list'),
        },
        {
          name: 'Voice Search',
          label: t('SIDEBAR.VOICE.SEARCH'),
          activeOn: ['voice_search'],
          to: accountScopedRoute('voice_search'),
        },
        {
          name: 'Voice Playground',
          label: t('SIDEBAR.VOICE.PLAYGROUND'),
          activeOn: ['voice_playground'],
          to: accountScopedRoute('voice_playground'),
        },
        {
          name: 'Voice Costs',
          label: t('SIDEBAR.VOICE.COSTS'),
          activeOn: ['voice_costs_dashboard'],
          to: accountScopedRoute('voice_costs_dashboard'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        // ── Account ──────────────────────────────────────
        {
          name: 'Settings Group Account',
          label: 'Account',
          children: [
            {
              name: 'Settings Account Settings',
              label: t('SIDEBAR.ACCOUNT_SETTINGS'),
              to: accountScopedRoute('general_settings_index'),
            },
            {
              name: 'Settings Custom Roles',
              label: t('SIDEBAR.CUSTOM_ROLES'),
              to: accountScopedRoute('custom_roles_list'),
            },
            {
              name: 'Settings Security',
              label: t('SIDEBAR.SECURITY'),
              to: accountScopedRoute('security_settings_index'),
            },
            {
              name: 'Settings Audit Logs',
              label: t('SIDEBAR.AUDIT_LOGS'),
              to: accountScopedRoute('auditlogs_list'),
            },
            {
              name: 'Settings Billing',
              label: t('SIDEBAR.BILLING'),
              to: accountScopedRoute('billing_settings_index'),
            },
            {
              name: 'Settings VIP',
              label: t('SIDEBAR.VIP'),
              to: accountScopedRoute('vip_settings_index'),
            },
          ],
        },
        // ── Team ──────────────────────────────────────
        {
          name: 'Settings Group Team',
          label: 'Team',
          children: [
            {
              name: 'Settings Agents',
              label: t('SIDEBAR.AGENTS'),
              to: accountScopedRoute('agent_list'),
            },
            {
              name: 'Settings Teams',
              label: t('SIDEBAR.TEAMS'),
              to: accountScopedRoute('settings_teams_list'),
            },
            {
              name: 'Settings Agent Assignment',
              label: t('SIDEBAR.AGENT_ASSIGNMENT'),
              to: accountScopedRoute('assignment_policy_index'),
            },
            {
              name: 'Settings Issue',
              label: t('SIDEBAR.ISSUE'),
              to: accountScopedRoute('settings_issues_list'),
            },
            {
              name: 'Settings Sla',
              label: t('SIDEBAR.SLA'),
              to: accountScopedRoute('sla_list'),
            },
          ],
        },
        // ── Channels ──────────────────────────────────────
        {
          name: 'Settings Group Channels',
          label: 'Channels',
          children: [
            {
              name: 'Settings Inboxes',
              label: t('SIDEBAR.INBOXES'),
              to: accountScopedRoute('settings_inbox_list'),
            },
            // Inbox Archive (Phase 1C). Hidden unless the inbox_archive
            // feature is enabled on this account so accounts that haven't
            // opted in see the original Channels submenu unchanged.
            ...(isInboxArchiveEnabled.value
              ? [
                  {
                    name: 'Settings Archived Inboxes',
                    label: t('SIDEBAR.ARCHIVED_INBOXES'),
                    to: accountScopedRoute('settings_archives_list'),
                    activeOn: [
                      'settings_archives_list',
                      'settings_archives_show',
                    ],
                  },
                ]
              : []),
            {
              name: 'Settings Telegram',
              label: t('SIDEBAR.TELEGRAM'),
              to: accountScopedRoute('settings_telegram_audience'),
              activeOn: [
                'settings_telegram_bot_structure',
                'settings_telegram_audience',
                'settings_telegram_statistics',
                'settings_telegram_autoposting',
                'settings_telegram_channels_groups',
                'settings_telegram_bot_settings',
              ],
              permissions: ['administrator', 'telegram_audience_manage'],
            },
            {
              name: 'Settings Agent Bots',
              label: t('SIDEBAR.AGENT_BOTS'),
              to: accountScopedRoute('agent_bots'),
            },
          ],
        },
        // ── Automation ──────────────────────────────────────
        {
          name: 'Settings Group Automation',
          label: 'Automation',
          children: [
            {
              name: 'Settings Automation',
              label: t('SIDEBAR.AUTOMATION'),
              to: accountScopedRoute('automation_list'),
            },
            {
              name: 'Settings Automation Flows',
              label: t('SIDEBAR.AUTOMATION_FLOWS'),
              to: accountScopedRoute('automation_flows_list'),
            },
          ],
        },
        // ── Content ──────────────────────────────────────
        {
          name: 'Settings Group Content',
          label: 'Content',
          children: [
            {
              name: 'Settings Macros',
              label: t('SIDEBAR.MACROS'),
              to: accountScopedRoute('macros_wrapper'),
            },
            {
              name: 'Settings Canned Responses',
              label: t('SIDEBAR.CANNED_RESPONSES'),
              to: accountScopedRoute('canned_list'),
            },
            {
              name: 'Settings Flex Templates',
              label: t('SIDEBAR.FLEX_TEMPLATES'),
              to: accountScopedRoute('flex_templates_list'),
              permissions: ['administrator', FLEX_TEMPLATE_PERMISSIONS],
            },
            {
              name: 'Settings Labels',
              label: t('SIDEBAR.LABELS'),
              to: accountScopedRoute('labels_list'),
            },
            {
              name: 'Settings Custom Attributes',
              label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
              to: accountScopedRoute('attributes_list'),
            },
            {
              name: 'Settings LINE Stickers',
              label: 'LINE Stickers',
              to: accountScopedRoute('line_stickers'),
            },
          ],
        },
        // ── Integrations ──────────────────────────────────────
        {
          name: 'Settings Group Integrations',
          label: 'Integrations',
          children: [
            {
              name: 'Settings Integrations',
              label: t('SIDEBAR.INTEGRATIONS'),
              to: accountScopedRoute('settings_applications'),
            },
          ],
        },
      ],
    },
  ].filter(item => {
    // Filter out AiLab if not enabled
    if (item.name === 'AiLab' && !isAiLabEnabled.value) {
      return false;
    }
    // Filter out Portals (Help Center) if not enabled
    if (item.name === 'Portals' && !isHelpCenterEnabled.value) {
      return false;
    }
    return true;
  });
});

const formatName = name => {
  if (!name) return '';
  return String(name)
    .split(' ')
    .map(segment => {
      const trimmed = segment.trim();
      if (!trimmed) return '';
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    })
    .join(' ');
};

// Filter menu items based on active category
const filteredMenuItems = computed(() => {
  const category = menuItems.value.find(
    item => item.name.toLowerCase() === normalizedActiveCategory.value
  );

  // If category has children, return the children as top-level items
  if (category?.children) {
    return category.children;
  }

  // If category has no children (like Inbox), return the category itself
  if (category) {
    return [category];
  }

  // Fallback to all menu items
  return menuItems.value;
});

// Get the current category label for the header
const currentCategoryLabel = computed(() => {
  const category = menuItems.value.find(
    item => item.name.toLowerCase() === normalizedActiveCategory.value
  );
  return category?.label || props.activeCategory;
});
</script>

<template>
  <aside
    v-if="!shouldHideSidebar"
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="flex flex-col text-sm fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 transition-transform duration-200 ease-in-out md:static md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:-translate-x-0 wlab-sidebar-wrapper overflow-hidden"
    style="background-color: var(--wlab-bg-sidebar); padding: 0 0 12px 0"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
      },
      isAiLabAssistantConfigRoute
        ? 'w-[200px] basis-[200px]'
        : isWideSidebarCategory
          ? 'w-[296px] basis-[296px]'
          : 'w-[200px] basis-[200px]',
      {
        'wlab-sidebar-wrapper--reports': isReportsLikeCategory,
        'wlab-sidebar-wrapper--campaigns': isCampaignsCategory,
        'wlab-sidebar-wrapper--qadashboard': isQaDashboardCategory,
        'wlab-sidebar-wrapper--settings': isSettingsCategory,
        'wlab-sidebar-wrapper--contacts': isContactsCategory,
        'wlab-sidebar-wrapper--ailab': isAiLabCategory,
        'wlab-sidebar-wrapper--voice': isVoiceCategory,
        'wlab-sidebar-wrapper--workforce': isWorkforceCategory,
        'wlab-sidebar-wrapper--conversation': isConversationCategory,
      },
    ]"
  >
    <div
      class="wlab-sidebar flex flex-col flex-1 overflow-hidden rounded-2xl"
      :class="{
        'wlab-sidebar--reports': isReportsLikeCategory,
        'wlab-sidebar--campaigns': isCampaignsCategory,
        'wlab-sidebar--qadashboard': isQaDashboardCategory,
        'wlab-sidebar--settings': isSettingsCategory,
        'wlab-sidebar--contacts': isContactsCategory,
        'wlab-sidebar--ailab': isAiLabCategory,
        'wlab-sidebar--voice': isVoiceCategory,
        'wlab-sidebar--workforce': isWorkforceCategory,
        'wlab-sidebar--conversation': isConversationCategory,
      }"
      style="background-color: var(--wlab-bg-sidebar-inner)"
    >
      <!-- Header Section - conditionally shown, fixed height to match conversation list header -->
      <section
        v-if="showHeader"
        class="grid gap-1 px-2 py-1 flex-shrink-0"
        style="height: 48px; max-height: 48px; min-height: 48px"
      >
        <div class="flex items-center min-w-0 gap-2 px-2">
          <div class="grid flex-shrink-0 size-6 place-content-center">
            <Logo class="size-4" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            prefer-top
            class="flex-grow min-w-0 -mx-1"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </div>
        <div class="flex gap-2 px-2 items-center">
          <RouterLink
            v-if="canAccessSearch"
            :to="{ name: 'search' }"
            class="flex items-center w-full gap-2 px-2 rounded-lg outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
            style="height: 1.5rem"
          >
            <span
              class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11"
            />
            <span class="flex-grow text-left">
              {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
            </span>
            <span
              class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
            >
              {{ searchShortcut }}
            </span>
          </RouterLink>
        </div>
      </section>

      <!-- Simplified Header when showHeader is false -->
      <section
        v-else
        class="wlab-sidebar-header-section px-3 flex items-center"
        :class="{
          'wlab-sidebar-header-section--reports': isReportsLikeCategory,
          'wlab-sidebar-header-section--campaigns': isCampaignsCategory,
          'wlab-sidebar-header-section--qadashboard': isQaDashboardCategory,
          'wlab-sidebar-header-section--settings': isSettingsCategory,
          'wlab-sidebar-header-section--contacts': isContactsCategory,
          'wlab-sidebar-header-section--ailab': isAiLabCategory,
          'wlab-sidebar-header-section--voice': isVoiceCategory,
          'wlab-sidebar-header-section--conversation': isConversationCategory,
        }"
      >
        <div class="flex items-center justify-between gap-2 w-full">
          <!-- Category Title -->
          <h2
            class="wlab-sidebar-category-title text-slate-800 dark:text-white"
          >
            {{ currentCategoryLabel }}
          </h2>
        </div>
      </section>

      <!-- Navigation -->

      <!-- ── Inbox settings navigation (replaces normal nav on settings_inbox_show) ── -->
      <nav v-if="isInboxSettingsRoute" class="is-nav flex-grow overflow-y-auto">
        <div class="is-nav-inner">
          <!-- Website widget group (web widget inboxes only) -->
          <template v-if="isWebWidgetInboxSettings">
            <div class="is-group">
              <div class="is-group-title">
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.WEBSITE_WIDGET_GROUP') }}
              </div>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active':
                    inboxSettingsActiveTab === 'widgetBuilder',
                }"
                @click="navigateToInboxTab('widgetBuilder')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.CUSTOMIZATION') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active': inboxSettingsActiveTab === 'eyeCatcher',
                }"
                @click="navigateToInboxTab('eyeCatcher')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.EYE_CATCHER') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active':
                    inboxSettingsActiveTab === 'bubbleLauncher',
                }"
                @click="navigateToInboxTab('bubbleLauncher')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.BUBBLE_LAUNCHER') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active': inboxSettingsActiveTab === 'poweredBy',
                }"
                @click="navigateToInboxTab('poweredBy')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.POWERED_BY') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active':
                    inboxSettingsActiveTab === 'welcomeScreen',
                }"
                @click="navigateToInboxTab('welcomeScreen')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.WELCOME_SCREEN') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active': inboxSettingsActiveTab === 'language',
                }"
                @click="navigateToInboxTab('language')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.LANGUAGE') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active':
                    inboxSettingsActiveTab === 'greetingCard',
                }"
                @click="navigateToInboxTab('greetingCard')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.GREETING_CARD') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active':
                    inboxSettingsActiveTab === 'availability',
                }"
                @click="navigateToInboxTab('availability')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.AVAILABILITY') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active':
                    inboxSettingsActiveTab === 'collaborators',
                }"
                @click="navigateToInboxTab('collaborators')"
              >
                {{ inboxCollaboratorsLabel }}
              </button>
            </div>

            <div class="is-group">
              <div class="is-group-title">
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.FORMS_GROUP') }}
              </div>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active':
                    inboxSettingsActiveTab === 'preChatForm',
                }"
                @click="navigateToInboxTab('preChatForm')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.PRE_CHAT_FORM') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active': inboxSettingsActiveTab === 'ticketForm',
                }"
                @click="navigateToInboxTab('ticketForm')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.TICKET_FORM') }}
              </button>
              <button
                class="is-nav-btn"
                :class="{
                  'is-nav-btn--active':
                    inboxSettingsActiveTab === 'postChatForm',
                }"
                @click="navigateToInboxTab('postChatForm')"
              >
                {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.POST_CHAT_FORM') }}
              </button>
            </div>
          </template>

          <!-- Chat settings (all inbox types) -->
          <div class="is-group">
            <div class="is-group-title">
              {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.CHAT_SETTINGS_GROUP') }}
            </div>
            <button
              class="is-nav-btn"
              :class="{
                'is-nav-btn--active':
                  inboxSettingsActiveTab === 'inbox_settings',
              }"
              @click="navigateToInboxTab('inbox_settings')"
            >
              {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.INBOX_SETTINGS') }}
            </button>
            <button
              v-if="!isWebWidgetInboxSettings"
              class="is-nav-btn"
              :class="{
                'is-nav-btn--active':
                  inboxSettingsActiveTab === 'collaborators',
              }"
              @click="navigateToInboxTab('collaborators')"
            >
              {{ inboxCollaboratorsLabel }}
            </button>
            <button
              class="is-nav-btn"
              :class="{
                'is-nav-btn--active':
                  inboxSettingsActiveTab === 'configuration',
              }"
              @click="navigateToInboxTab('configuration')"
            >
              {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.CONFIGURATION') }}
            </button>
            <button
              class="is-nav-btn"
              :class="{
                'is-nav-btn--active':
                  inboxSettingsActiveTab === 'botConfiguration',
              }"
              @click="navigateToInboxTab('botConfiguration')"
            >
              {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.BOT_CONFIGURATION') }}
            </button>
            <button
              class="is-nav-btn"
              :class="{
                'is-nav-btn--active':
                  inboxSettingsActiveTab === 'inactivityTimeouts',
              }"
              @click="navigateToInboxTab('inactivityTimeouts')"
            >
              {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.INACTIVITY_TIMEOUTS') }}
            </button>
            <button
              class="is-nav-btn"
              :class="{
                'is-nav-btn--active': inboxSettingsActiveTab === 'aiVoice',
              }"
              @click="navigateToInboxTab('aiVoice')"
            >
              {{ t('INBOX_MGMT.INBOX_SETTINGS_NAV.AI_VOICE') }}
            </button>
          </div>
        </div>
      </nav>

      <!-- ── AiLab assistant config navigation (replaces normal nav in assistant v2) ── -->
      <nav
        v-else-if="isAiLabAssistantConfigRoute"
        class="is-nav flex-grow overflow-y-auto"
      >
        <div class="is-nav-inner">
          <div
            v-for="group in ailabAssistantSidebarGroups"
            :key="group.title"
            class="is-group"
            :class="{ 'is-group--advanced': group.title === 'Advanced' }"
          >
            <div class="is-group-title">{{ group.title }}</div>
            <button
              v-for="item in group.items"
              :key="item.key"
              class="is-nav-btn"
              :class="{
                'is-nav-btn--active': ailabAssistantActiveTab === item.key,
              }"
              @click="navigateToAiLabAssistantTab(item.key)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>
      </nav>

      <!-- ── Normal navigation ── -->
      <nav
        v-else
        class="wlab-sidebar-nav grid flex-grow gap-2 px-2 pt-3 pb-16 overflow-y-scroll"
      >
        <ul class="wlab-sidebar-nav-list flex flex-col gap-1.5 m-0 list-none">
          <SidebarGroup
            v-for="item in showHeader ? menuItems : filteredMenuItems"
            :key="item.name"
            v-bind="item"
          />
        </ul>
      </nav>

      <!-- Footer Section - only show when showHeader is true -->
      <section
        v-if="showHeader"
        class="p-1 border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)] flex-shrink-0 flex justify-between gap-2 items-center"
      >
        <SidebarProfileMenu
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </section>
    </div>
  </aside>
</template>

<style>
/* ── Inbox-settings sidebar navigation — aligned to Reports leaf styling ── */
.is-nav {
  flex-grow: 1;
  overflow-y: auto;
  padding: 8px 8px 64px 16px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.14) transparent;
}

.is-nav::-webkit-scrollbar {
  width: 1px !important;
  height: 1px !important;
}

.is-nav::-webkit-scrollbar-track {
  background: transparent !important;
}

.is-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.14) !important;
  border-radius: 999px !important;
}

.is-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.22) !important;
}

.is-nav-inner {
  display: flex;
  flex-direction: column;
  gap: 0;
  list-style: none;
  margin: 0;
  padding: 0;
}

.is-group {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-bottom: 6px;
}

.is-group-title {
  font-family: 'Excon', sans-serif !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  min-height: 32px;
  height: auto;
  padding: 5.5px 12px !important;
  border-radius: 8px;
  display: flex;
  align-items: center;
  cursor: default;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The "Advanced" group holds power-user tools. Demote it visually so a
   first-time admin's eye is drawn to Identity / Knowledge / Channels.
   Section descriptions were moved to each page's content header instead of
   the sidebar to keep navigation scannable. */
.is-group--advanced {
  opacity: 0.85;
}

.is-group--advanced .is-group-title {
  color: #a8aaae;
}

.is-nav-btn {
  font-family: 'Excon', sans-serif !important;
  font-size: 11px !important;
  font-weight: 500;
  display: flex;
  align-items: center;
  min-height: 32px;
  height: auto;
  border-radius: 8px;
  padding: 5.5px 12px;
  width: calc(100% - 8px);
  margin-left: 8px;
  max-width: none;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition:
    background 0.12s ease,
    color 0.12s ease;
}

/* Light mode */
body:not(.dark) .is-nav {
  scrollbar-color: rgba(0, 0, 0, 0.12) transparent;
}

body:not(.dark) .is-nav::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12) !important;
}

body:not(.dark) .wlab-sidebar .is-group-title {
  color: #0f172a !important;
}

body:not(.dark) .wlab-sidebar .is-group--advanced .is-group-title {
  color: rgba(15, 23, 42, 0.55) !important;
}

body:not(.dark) .wlab-sidebar .is-nav-btn {
  color: rgba(15, 23, 42, 0.72) !important;
}

body:not(.dark) .wlab-sidebar .is-nav-btn:hover {
  background: rgba(15, 23, 42, 0.08) !important;
  color: #0f172a !important;
}

body:not(.dark) .wlab-sidebar .is-nav-btn--active {
  background: #e5e7eb !important;
  color: #0f172a !important;
  font-weight: 600 !important;
}

/* Dark mode - same tone as Reports */
body.dark .wlab-sidebar .is-group-title {
  color: #ffffff !important;
}

body.dark .wlab-sidebar .is-nav-btn {
  color: rgba(255, 255, 255, 0.68) !important;
}

body.dark .wlab-sidebar .is-nav-btn:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #ffffff !important;
}

body.dark .wlab-sidebar .is-nav-btn--active {
  background: #2b2c31 !important;
  color: #ffffff !important;
  font-weight: 600 !important;
}
/* ─────────────────────────────────────────────────────────────────────────── */

.wlab-sidebar {
  font-family: 'Excon', sans-serif !important;
}

.wlab-sidebar *,
.wlab-sidebar a,
.wlab-sidebar button,
.wlab-sidebar span,
.wlab-sidebar div,
.wlab-sidebar input,
.wlab-sidebar .text-xs,
.wlab-sidebar .text-sm,
.wlab-sidebar .text-base,
.wlab-sidebar .text-lg {
  font-family: 'Excon', sans-serif !important;
  font-size: 11px !important;
}

/* Smaller icons */
.wlab-sidebar [class*='i-lucide-'],
.wlab-sidebar [class*='i-ph-'],
.wlab-sidebar [class*='i-woot-'] {
  width: 14px !important;
  height: 14px !important;
}

/* 亮色主题 - 深灰色文字 */
body:not(.dark) .wlab-sidebar * {
  color: #1e293b !important;
}

/* 亮色主题 - 更深的边框 */
body:not(.dark) .wlab-sidebar {
  border: 1px solid #e2e8f0;
}

/* 暗色主题 - 白色文字 */
body.dark .wlab-sidebar * {
  color: #ffffff !important;
}

/* 暗色主题 - sidebar 背景 */
body.dark .wlab-sidebar {
  background-color: #121213 !important;
}

/* 暗色主题 - sidebar header 背景 */
body.dark .wlab-sidebar-header-section {
  background-color: #121213 !important;
}

/* Sidebar compose button - 1.5rem size */
.sidebar-compose-btn {
  width: 1.5rem !important;
  height: 1.5rem !important;
  min-width: 1.5rem !important;
  min-height: 1.5rem !important;
  padding: 0 !important;
}

/* Sidebar header - match other headers */
.wlab-sidebar-header-section {
  height: 56px;
  min-height: 56px;
  background-color: var(--wlab-header-bg);
  border-bottom: 1px solid var(--wlab-header-border);
}

.wlab-sidebar-category-title {
  margin: 0;
  font-size: 20px;
  line-height: 1.25;
  font-weight: 700;
}

/* Latest sidebar dark theme (match Reports, keep widths per-category) */
body.dark .wlab-sidebar-wrapper--reports,
body.dark .wlab-sidebar-wrapper--campaigns,
body.dark .wlab-sidebar-wrapper--qadashboard,
body.dark .wlab-sidebar-wrapper--settings,
body.dark .wlab-sidebar-wrapper--contacts,
body.dark .wlab-sidebar-wrapper--ailab,
body.dark .wlab-sidebar-wrapper--workforce,
body.dark .wlab-sidebar-wrapper--conversation {
  background-color: #000000 !important;
}

body.dark .wlab-sidebar--reports,
body.dark .wlab-sidebar--campaigns,
body.dark .wlab-sidebar--qadashboard,
body.dark .wlab-sidebar--settings,
body.dark .wlab-sidebar--contacts,
body.dark .wlab-sidebar--ailab,
body.dark .wlab-sidebar--workforce,
body.dark .wlab-sidebar--conversation {
  background-color: #000000 !important;
  border-radius: 0 !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-header-section,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-header-section,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-header-section,
body.dark .wlab-sidebar--settings .wlab-sidebar-header-section,
body.dark .wlab-sidebar--contacts .wlab-sidebar-header-section,
body.dark .wlab-sidebar--ailab .wlab-sidebar-header-section,
body.dark .wlab-sidebar--workforce .wlab-sidebar-header-section,
body.dark .wlab-sidebar--conversation .wlab-sidebar-header-section {
  background-color: #000000 !important;
  border-bottom: 0 !important;
  padding: 0 16px !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-category-title,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-category-title,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-category-title,
body.dark .wlab-sidebar--settings .wlab-sidebar-category-title,
body.dark .wlab-sidebar--contacts .wlab-sidebar-category-title,
body.dark .wlab-sidebar--ailab .wlab-sidebar-category-title,
body.dark .wlab-sidebar--workforce .wlab-sidebar-category-title,
body.dark .wlab-sidebar--conversation .wlab-sidebar-category-title {
  font-size: 18px !important;
  line-height: 1.2 !important;
  color: #ffffff !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-nav,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-nav,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-nav,
body.dark .wlab-sidebar--settings .wlab-sidebar-nav,
body.dark .wlab-sidebar--contacts .wlab-sidebar-nav,
body.dark .wlab-sidebar--ailab .wlab-sidebar-nav,
body.dark .wlab-sidebar--workforce .wlab-sidebar-nav {
  gap: 0 !important;
  padding: 8px 8px 16px 16px !important;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
}

body.dark .wlab-sidebar--conversation .wlab-sidebar-nav {
  gap: 0 !important;
}

/* Sidebar scrollbar - ultra-thin, transparent, no arrow buttons */
.wlab-sidebar-nav {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.08) transparent;
}

.wlab-sidebar-nav::-webkit-scrollbar {
  width: 1px !important;
  height: 1px !important;
}

.wlab-sidebar-nav::-webkit-scrollbar-track {
  background: transparent !important;
}

.wlab-sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.08) !important;
  border-radius: 999px !important;
}

.wlab-sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.16) !important;
}

.wlab-sidebar-nav::-webkit-scrollbar-button {
  width: 0 !important;
  height: 0 !important;
  display: none !important;
  background: transparent !important;
}

.wlab-sidebar-nav::-webkit-scrollbar-button:single-button,
.wlab-sidebar-nav::-webkit-scrollbar-button:start:decrement,
.wlab-sidebar-nav::-webkit-scrollbar-button:end:increment {
  width: 0 !important;
  height: 0 !important;
  display: none !important;
  background: transparent !important;
}

.wlab-sidebar-nav::-webkit-scrollbar-corner {
  background: transparent !important;
}

body.dark .wlab-sidebar-nav {
  scrollbar-color: rgba(255, 255, 255, 0.08) transparent;
}

body.dark .wlab-sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.08) !important;
}

body.dark .wlab-sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.14) !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-nav-list,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-nav-list,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-nav-list,
body.dark .wlab-sidebar--settings .wlab-sidebar-nav-list,
body.dark .wlab-sidebar--contacts .wlab-sidebar-nav-list,
body.dark .wlab-sidebar--ailab .wlab-sidebar-nav-list,
body.dark .wlab-sidebar--workforce .wlab-sidebar-nav-list,
body.dark .wlab-sidebar--conversation .wlab-sidebar-nav-list {
  gap: 0 !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-group,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-group,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-group,
body.dark .wlab-sidebar--settings .wlab-sidebar-group,
body.dark .wlab-sidebar--contacts .wlab-sidebar-group,
body.dark .wlab-sidebar--ailab .wlab-sidebar-group,
body.dark .wlab-sidebar--workforce .wlab-sidebar-group,
body.dark .wlab-sidebar--conversation .wlab-sidebar-group {
  gap: 0 !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-header,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-header,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-header,
body.dark .wlab-sidebar--settings .wlab-sidebar-header,
body.dark .wlab-sidebar--contacts .wlab-sidebar-header,
body.dark .wlab-sidebar--ailab .wlab-sidebar-header,
body.dark .wlab-sidebar--workforce .wlab-sidebar-header,
body.dark .wlab-sidebar--conversation .wlab-sidebar-header {
  min-height: 32px !important;
  height: auto !important;
  border-radius: 8px !important;
  padding: 5.5px 12px !important;
  background: transparent !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-header,
body.dark .wlab-sidebar--reports .wlab-sidebar-header *,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-header,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-header *,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-header,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-header *,
body.dark .wlab-sidebar--settings .wlab-sidebar-header,
body.dark .wlab-sidebar--settings .wlab-sidebar-header *,
body.dark .wlab-sidebar--contacts .wlab-sidebar-header,
body.dark .wlab-sidebar--contacts .wlab-sidebar-header *,
body.dark .wlab-sidebar--ailab .wlab-sidebar-header,
body.dark .wlab-sidebar--ailab .wlab-sidebar-header *,
body.dark .wlab-sidebar--workforce .wlab-sidebar-header,
body.dark .wlab-sidebar--workforce .wlab-sidebar-header *,
body.dark .wlab-sidebar--conversation .wlab-sidebar-header,
body.dark .wlab-sidebar--conversation .wlab-sidebar-header * {
  color: #ffffff !important;
  font-size: 11px !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-header:hover,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-header:hover,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-header:hover,
body.dark .wlab-sidebar--settings .wlab-sidebar-header:hover,
body.dark .wlab-sidebar--contacts .wlab-sidebar-header:hover,
body.dark .wlab-sidebar--ailab .wlab-sidebar-header:hover,
body.dark .wlab-sidebar--workforce .wlab-sidebar-header:hover,
body.dark .wlab-sidebar--conversation .wlab-sidebar-header:hover {
  background: rgba(255, 255, 255, 0.08) !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-header.bg-n-alpha-2,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-header.bg-n-alpha-2,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-header.bg-n-alpha-2,
body.dark .wlab-sidebar--settings .wlab-sidebar-header.bg-n-alpha-2,
body.dark .wlab-sidebar--contacts .wlab-sidebar-header.bg-n-alpha-2,
body.dark .wlab-sidebar--ailab .wlab-sidebar-header.bg-n-alpha-2,
body.dark .wlab-sidebar--workforce .wlab-sidebar-header.bg-n-alpha-2,
body.dark .wlab-sidebar--conversation .wlab-sidebar-header.bg-n-alpha-2 {
  background: #2b2c31 !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-header .i-lucide-chevron-right,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-header .i-lucide-chevron-right,
body.dark
  .wlab-sidebar--qadashboard
  .wlab-sidebar-header
  .i-lucide-chevron-right,
body.dark .wlab-sidebar--settings .wlab-sidebar-header .i-lucide-chevron-right,
body.dark .wlab-sidebar--contacts .wlab-sidebar-header .i-lucide-chevron-right,
body.dark .wlab-sidebar--ailab .wlab-sidebar-header .i-lucide-chevron-right,
body.dark .wlab-sidebar--workforce .wlab-sidebar-header .i-lucide-chevron-right,
body.dark
  .wlab-sidebar--conversation
  .wlab-sidebar-header
  .i-lucide-chevron-right {
  opacity: 0.7;
}

body.dark .wlab-sidebar--reports .sidebar-group-children,
body.dark .wlab-sidebar--campaigns .sidebar-group-children,
body.dark .wlab-sidebar--qadashboard .sidebar-group-children,
body.dark .wlab-sidebar--settings .sidebar-group-children,
body.dark .wlab-sidebar--contacts .sidebar-group-children,
body.dark .wlab-sidebar--ailab .sidebar-group-children,
body.dark .wlab-sidebar--workforce .sidebar-group-children,
body.dark .wlab-sidebar--conversation .sidebar-group-children {
  margin-top: 0 !important;
  padding-bottom: 6px !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-leaf,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf,
body.dark .wlab-sidebar--conversation .wlab-sidebar-leaf {
  margin-left: 8px !important;
  padding-left: 0 !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-leaf::before,
body.dark .wlab-sidebar--reports .wlab-sidebar-leaf::after,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf::before,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf::after,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf::before,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf::after,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf::before,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf::after,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf::before,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf::after,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf::before,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf::after,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf::before,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf::after {
  display: none !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf-link {
  max-width: none !important;
  min-height: 32px !important;
  height: auto !important;
  border-radius: 8px !important;
  padding: 5.5px 12px !important;
  background: transparent !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--reports .wlab-sidebar-leaf-link *,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf-link *,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf-link *,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf-link *,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf-link *,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf-link *,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf-link,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf-link * {
  color: rgba(255, 255, 255, 0.68) !important;
  font-size: 11px !important;
  font-weight: 500 !important;
}

body.dark
  .wlab-sidebar--reports
  .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2),
body.dark
  .wlab-sidebar--campaigns
  .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2),
body.dark
  .wlab-sidebar--qadashboard
  .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2),
body.dark
  .wlab-sidebar--settings
  .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2),
body.dark
  .wlab-sidebar--contacts
  .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2),
body.dark
  .wlab-sidebar--ailab
  .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2),
body.dark
  .wlab-sidebar--workforce
  .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2),
body.dark
  .wlab-sidebar--conversation
  .wlab-sidebar-leaf-link:hover:not(.active):not(.bg-n-alpha-2) {
  background: rgba(255, 255, 255, 0.08) !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-leaf-link.active,
body.dark .wlab-sidebar--reports .wlab-sidebar-leaf-link.bg-n-alpha-2,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf-link.active,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf-link.bg-n-alpha-2,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf-link.active,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf-link.bg-n-alpha-2,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf-link.active,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf-link.bg-n-alpha-2,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf-link.active,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf-link.bg-n-alpha-2,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf-link.active,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf-link.bg-n-alpha-2,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf-link.active,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf-link.bg-n-alpha-2,
body.dark .wlab-sidebar--conversation .wlab-sidebar-leaf-link.active,
body.dark .wlab-sidebar--conversation .wlab-sidebar-leaf-link.bg-n-alpha-2 {
  background: #2b2c31 !important;
}

body.dark .wlab-sidebar--reports .wlab-sidebar-leaf-link.active *,
body.dark .wlab-sidebar--reports .wlab-sidebar-leaf-link.bg-n-alpha-2 *,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf-link.active *,
body.dark .wlab-sidebar--campaigns .wlab-sidebar-leaf-link.bg-n-alpha-2 *,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf-link.active *,
body.dark .wlab-sidebar--qadashboard .wlab-sidebar-leaf-link.bg-n-alpha-2 *,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf-link.active *,
body.dark .wlab-sidebar--settings .wlab-sidebar-leaf-link.bg-n-alpha-2 *,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf-link.active *,
body.dark .wlab-sidebar--contacts .wlab-sidebar-leaf-link.bg-n-alpha-2 *,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf-link.active *,
body.dark .wlab-sidebar--ailab .wlab-sidebar-leaf-link.bg-n-alpha-2 *,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf-link.active *,
body.dark .wlab-sidebar--workforce .wlab-sidebar-leaf-link.bg-n-alpha-2 *,
body.dark .wlab-sidebar--conversation .wlab-sidebar-leaf-link.active *,
body.dark .wlab-sidebar--conversation .wlab-sidebar-leaf-link.bg-n-alpha-2 * {
  color: #ffffff !important;
  font-weight: 600 !important;
}
</style>
